/* eslint-disable jsx-a11y/anchor-is-valid */
import MUIDataTable, {
  MUIDataTableMeta,
  MUIDataTableOptions,
} from 'mui-datatables';
import { useQuery } from '@tanstack/react-query';

import { IInstitutionHead, IInstitutionHeadForm } from '../core/models';
import { SGetUsers } from '../../../apis/app.service';

import CreateEditUser from './CreateEditUser';
import ConfirmPasswordReset from './ConfirmPasswordReset';
import UpdatePassword from './UpdatePassword';
import ConfirmUserDelete from './ConfirmUserDelete';
import LoadingSpinner from '../../sharedcomponents/LoadingSpinner';
import { useUsersPage } from '../../../store/UsersProvider';
import AppButton from '../../sharedcomponents/Buttons/AppButton/AppButton';
import { useAuth } from '../../../store/AuthProvider';
import ConfirmInstitutionActive from './ConfirmInstitutionActive';

export default function UsersWrapper() {
  const { institutionHead, setInstitutionHead } = useUsersPage();
  const { user } = useAuth();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      SGetUsers(user!._id, {
        admin_Account: user!._id,
        role: 'institution_head,institution_global_head',
      }),
  });

  // Umbrella institution _id -> name, so each office row can show what it
  // rolls up into without a second request.
  const umbrellaNames = new Map<string, string>(
    (data ?? [])
      .filter((row: any) => row.role === 'institution_global_head')
      .map((row: any) => [String(row.institution?._id), row.institution?.name])
  );

  const newInstitution = (role: string): IInstitutionHeadForm => ({
    email: '',
    organisationName: '',
    firstname: '',
    lastname: '',
    position: '',
    phoneNumber: '',
    type: 'external',
    role,
    parentInstitution: '',
    permissions: {
      interviews: true,
      devices: false,
      reports: true,
    },
  });

  const columns = [
    {
      name: 'user_Id',
      label: 'ID',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'name',
      label: 'Credentials',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
          // tableMeta.tableData[rowIndex] is mui-datatables' internal,
          // column-ordered array (not a keyed row object) — index into the
          // actual data we gave the table instead.
          const row = data[tableMeta.rowIndex];
          return (
            <div>
              <div className="row">
                <p className="col-2">
                  <b>Name:</b>
                </p>
                <p className="col-10">{row.name}</p>
              </div>
              <div className="row">
                <p className="col-2">
                  <b>Email:</b>
                </p>
                <p className="col-10">{row.email}</p>
              </div>
              <div className="row">
                <p className="col-2">
                  <b>Phone:</b>
                </p>
                <p className="col-10">{row.phoneNumber}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'institution',
      label: 'Name',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
          return <span>{value?.name}</span>;
        },
      },
    },
    {
      name: 'role',
      label: 'Type',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
          // tableMeta.tableData[rowIndex] is mui-datatables' internal,
          // column-ordered array (not a keyed row object) — index into the
          // actual data we gave the table instead.
          const row: any = data[tableMeta.rowIndex];

          if (value === 'institution_global_head') {
            return <span className="badge badge-light-primary">Umbrella</span>;
          }

          const umbrella = umbrellaNames.get(String(row.institution?.parent));
          return (
            <span>
              Institution
              {umbrella ? (
                <span className="text-muted d-block">under {umbrella}</span>
              ) : null}
            </span>
          );
        },
      },
    },
    {
      name: 'active',
      label: 'Active',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: boolean | undefined, tableMeta: any) => {
          // tableMeta.tableData[rowIndex] is mui-datatables' internal,
          // column-ordered array (not a keyed row object) — index into the
          // actual data we gave the table instead.
          const row = data[tableMeta.rowIndex];
          return (
            <div
              onClick={() => {
                let user = {
                  ...institutionHead,
                  data: {
                    ...institutionHead.data,
                    id: row._id,
                    active: row.active,
                    institution: row.institution,
                  },
                };
                setInstitutionHead(user);
              }}
            >
              <div
                className="form-check form-check-solid form-switch"
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_confirm_active"
              >
                <input
                  className="form-check-input"
                  style={{
                    height: '20px',
                    width: '35px',
                  }}
                  type="checkbox"
                  checked={value}
                />
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'position',
      label: 'Position',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
          return <span>{value}</span>;
        },
      },
    },
    {
      name: '',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        empty: false,
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
          // tableMeta.tableData[rowIndex] is mui-datatables' internal,
          // column-ordered array (not a keyed row object) — index into the
          // actual data we gave the table instead, or every field below
          // silently reads undefined and the edit form opens blank.
          const row = data[tableMeta.rowIndex];

          // name and phoneNumber are not guaranteed on every account (older
          // records were created straight in the database), so don't assume
          // they are there — a missing one must not take the table down.
          const [firstname = '', lastname = ''] = (row.name || '').split(' ');

          // Named formData, not data — a same-scope const named data would
          // shadow (and TDZ-break) the outer useQuery `data` array that
          // `row` is read from above.
          const formData: IInstitutionHeadForm = {
            id: row._id,
            email: row.email,
            organisationName: row.institution?.name || '',
            firstname,
            lastname,
            position: row.position || '',
            phoneNumber: (row.phoneNumber || '').slice(4),
            type: row.type,
            role: row.role,
            department: row.department,
            institution: row.institution,
            parentInstitution: row.institution?.parent
              ? String(row.institution.parent)
              : '',
            permissions: row.institution?.permissions
              ? row.institution.permissions
              : {
                  interviews: true,
                  devices: false,
                  reports: true,
                },
          };
          return (
            <>
              {/* begin::Menu */}
              <div
                className="d-flex flex-row align-content-center w-125px py-4"
                data-kt-menu="true"
              >
                {/* begin::Menu item */}
                <span
                  className="btn-icon-default pe-5 icon-color"
                  onClick={() => {
                    setInstitutionHead({ data: formData, action: 'edit' });
                  }}
                  data-bs-toggle="modal"
                  data-bs-target="#kt_modal_create_user"
                >
                  <i className="fas fa-edit btn-icon-default" title="Edit"></i>
                </span>
                {/* end::Menu item */}

                {/* begin::Menu item */}
                <span
                  className="btn-icon-default pe-5 icon-color"
                  onClick={() => {
                    setInstitutionHead({ data: formData, action: 'delete' });
                  }}
                  data-bs-toggle="modal"
                  data-bs-target="#kt_modal_confirm_delete"
                >
                  <i
                    className="fas fa-trash btn-icon-default"
                    title="Delete"
                  ></i>
                </span>
                <span
                  className="btn-icon-default pe-5 icon-color"
                  data-bs-toggle="modal"
                  data-bs-target="#kt_modal_confirm_reset"
                  onClick={() => {
                    setInstitutionHead({
                      data: formData,
                      action: 'change-password',
                    });
                  }}
                >
                  <i
                    className="fas fa-key btn-icon-default"
                    title="password reset"
                  ></i>
                </span>
                {/* end::Menu item */}
              </div>
              {/* end::Menu */}
            </>
          );
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    caseSensitive: false,
    filterType: 'dropdown',
    viewColumns: false,
    responsive: 'standard',
    jumpToPage: true,
    downloadOptions: { filename: 'Users.csv', separator: ',' },
    elevation: 1,
    // Rendered after the built-in toolbar icons. Keep this a fragment with
    // inline spacing — a block-level wrapper drops the buttons out of the
    // toolbar's row and onto a line of their own.
    customToolbar: () => {
      return (
        <>
          <AppButton
            type="button"
            btnType="secondary"
            data-bs-toggle="modal"
            data-bs-target="#kt_modal_create_user"
            style={{ width: '190px', marginLeft: '10px' }}
            onClick={() =>
              setInstitutionHead({
                action: 'create',
                data: newInstitution('institution_global_head'),
              })
            }
          >
            <i className="fa-solid fa-plus fa-lg me-2"></i>
            <span>Add Umbrella</span>
          </AppButton>

          <AppButton
            type="button"
            btnType="success"
            data-bs-toggle="modal"
            data-bs-target="#kt_modal_create_user"
            style={{ width: '170px', marginLeft: '10px' }}
            onClick={() =>
              setInstitutionHead({
                action: 'create',
                data: newInstitution('institution_head'),
              })
            }
          >
            <i className="fa-solid fa-plus fa-lg me-2"></i>
            <span>Add Institution</span>
          </AppButton>
        </>
      );
    },
    customSearchRender: (searchText, handleSearch) => {
      return (
        <div className="card-title">
          <div className="d-flex align-items-center position-relative my-1">
            <input
              type="text"
              data-kt-user-table-filter="search"
              className="form-control form-control-solid ps-14 mt-3"
              style={{ width: '250px' }}
              placeholder="Search user"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>
      );
    },
  };
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <div className="d-flex justify-center align-content-center">
        <p>Oops! Something Went Wrong! Try Again!!!</p>
        <p>{error.message}</p>
      </div>
    );
  }
  return (
    <div className="users">
      <MUIDataTable
        title="Institutions"
        data={data}
        columns={columns}
        options={options}
      />
      {/* Modals */}
      <CreateEditUser />
      <ConfirmUserDelete />
      <UpdatePassword />
      <ConfirmPasswordReset />
      <ConfirmInstitutionActive />
    </div>
  );
}
